import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { financialPlanningPages } from '@app/const/route.const';
import {
  MeetingGuidanceWidget,
  useGetMeetingGuidanceBuilderDraftQuery,
  useGetMeetingGuidanceQuery,
  useGetMeetingGuidanceTemplatesQuery,
  useSaveMeetingGuidanceBuilderDraftMutation,
  useSaveMeetingGuidanceIntakeMutation,
} from '@app/features/meetingGuidance';
import type {
  MeetingGuidanceBuilderAgendaSection,
  MeetingGuidanceBuilderDraft,
  MeetingGuidanceBuilderSectionDraft,
  MeetingIntakePriority,
  MeetingIntakeSaveEntry,
} from '@app/features/meetingGuidance';

const buildEntries = (
  value: string,
  priority: MeetingIntakePriority,
): MeetingIntakeSaveEntry[] =>
  value
    .split('\n')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((text) => ({ text, priority }));

const entriesToLines = (entries: { text: string }[]) =>
  entries.map((entry) => entry.text).join('\n');

const buildSectionDraft = (
  section: MeetingGuidanceBuilderAgendaSection,
): MeetingGuidanceBuilderSectionDraft => ({
  section_id: section.section_id,
  agenda_item_id: section.agenda_item_id,
  title: section.title,
  order: section.order,
  is_visible: section.is_visible,
  estimated_minutes: section.estimated_minutes,
  client_facing_summary: section.client_facing_summary,
  advisor_talking_points: section.advisor_talking_points,
  selected_question_ids: [],
  selected_reminder_ids: [],
  selected_material_ids: [],
  custom_items: [],
});

const buildDraftSavePayload = (
  builderDraft: MeetingGuidanceBuilderDraft | undefined,
  primaryMeetingObjective: string,
  agendaSections: MeetingGuidanceBuilderSectionDraft[],
) => ({
  status: builderDraft?.status ?? 'draft',
  prep: {
    primary_meeting_objective: primaryMeetingObjective.trim() || null,
  },
  sections: agendaSections,
  questions: builderDraft?.questions ?? [],
  notes: builderDraft?.notes ?? [],
  materials: builderDraft?.materials ?? [],
  generated_suggestions: builderDraft?.generated_suggestions ?? [],
  metadata: {
    source_guidance_generated_at:
      builderDraft?.metadata.source_guidance_generated_at ?? null,
    source_signal_registry_version:
      builderDraft?.metadata.source_signal_registry_version ?? null,
  },
});

export const MeetingGuidancePage = () => {
  const { planResultRootId, planResultTypeId } = useParams<{
    planResultRootId: string;
    planResultTypeId: string;
  }>();
  const navigate = useNavigate();
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [clientTopics, setClientTopics] = useState('');
  const [advisorPriorities, setAdvisorPriorities] = useState('');
  const [primaryMeetingObjective, setPrimaryMeetingObjective] = useState('');
  const [agendaSections, setAgendaSections] = useState<
    MeetingGuidanceBuilderSectionDraft[]
  >([]);
  const [clientPriority, setClientPriority] = useState<MeetingIntakePriority>('high');
  const [advisorPriority, setAdvisorPriority] = useState<MeetingIntakePriority>('medium');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isPrepOpen, setIsPrepOpen] = useState(true);
  const [hasInitializedPrep, setHasInitializedPrep] = useState(false);
  const [hasInitializedAgenda, setHasInitializedAgenda] = useState(false);
  const rootId = planResultRootId ?? '';
  const typeId = planResultTypeId ?? '';
  const { data: guidanceData } = useGetMeetingGuidanceQuery(
    {
      planResultRootId: rootId,
      planResultTypeId: typeId,
    },
    { skip: !planResultRootId || !planResultTypeId },
  );
  const { data: templatesData, isLoading: templatesLoading } =
    useGetMeetingGuidanceTemplatesQuery(
      {
        planResultRootId: rootId,
        planResultTypeId: typeId,
      },
      { skip: !planResultRootId || !planResultTypeId },
    );
  const { data: builderDraft } = useGetMeetingGuidanceBuilderDraftQuery(
    {
      planResultRootId: rootId,
      planResultTypeId: typeId,
    },
    { skip: !planResultRootId || !planResultTypeId },
  );
  const [saveIntake, { isLoading: isSaving, isError: isSaveError, error: saveError }] =
    useSaveMeetingGuidanceIntakeMutation();
  const [saveBuilderDraft] = useSaveMeetingGuidanceBuilderDraftMutation();

  const savedIntake = guidanceData?.meeting_intake;
  const hasSavedIntake =
    Boolean(savedIntake?.template_id) ||
    Boolean(savedIntake?.client_topics.length) ||
    Boolean(savedIntake?.advisor_priorities.length);
  const savedTemplateName = savedIntake?.selected_template?.name ?? 'default agenda';
  const savedClientTopicCount = savedIntake?.client_topics.length ?? 0;
  const savedAdvisorPriorityCount = savedIntake?.advisor_priorities.length ?? 0;
  const isMeetingReady =
    guidanceData?.guidance_readiness.can_show_meeting_ready_output ?? false;
  const savedAdvisorPriorityLabel =
    savedAdvisorPriorityCount === 1 ? 'priority' : 'priorities';
  const prepSummary = hasSavedIntake
    ? [
        `Using ${savedTemplateName} with ${savedClientTopicCount} client topic(s)`,
        `and ${savedAdvisorPriorityCount} advisor ${savedAdvisorPriorityLabel}.`,
      ].join(' ')
    : 'No explicit meeting intent has been saved yet.';

  useEffect(() => {
    if (!savedIntake || hasInitializedPrep) {
      return;
    }

    setSelectedTemplateId(savedIntake.template_id ?? '');
    setClientTopics(entriesToLines(savedIntake.client_topics));
    setAdvisorPriorities(entriesToLines(savedIntake.advisor_priorities));
    setClientPriority(savedIntake.client_topics[0]?.priority ?? 'high');
    setAdvisorPriority(savedIntake.advisor_priorities[0]?.priority ?? 'medium');
    setIsPrepOpen(!hasSavedIntake);
    setHasInitializedPrep(true);
  }, [hasInitializedPrep, hasSavedIntake, savedIntake]);

  useEffect(() => {
    if (!builderDraft || hasInitializedAgenda) {
      return;
    }

    setPrimaryMeetingObjective(
      builderDraft.prep.primary_meeting_objective ?? '',
    );

    const projectedSections = guidanceData?.builder_agenda.sections ?? [];
    const initialSections =
      builderDraft.sections.length > 0
        ? builderDraft.sections
        : projectedSections.map(buildSectionDraft);
    if (initialSections.length === 0) {
      return;
    }
    setAgendaSections(initialSections);
    setHasInitializedAgenda(true);
  }, [builderDraft, guidanceData?.builder_agenda.sections, hasInitializedAgenda]);

  if (!planResultRootId || !planResultTypeId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="par02Regular">
          Missing required parameters: planResultRootId and planResultTypeId
        </Typography>
      </Box>
    );
  }

  const handleSave = async () => {
    setSaveSuccess(false);
    try {
      await saveIntake({
        planResultRootId,
        planResultTypeId,
        intake: {
          template_id: selectedTemplateId || null,
          client_topics: buildEntries(clientTopics, clientPriority),
          advisor_priorities: buildEntries(advisorPriorities, advisorPriority),
        },
      }).unwrap();
      await saveBuilderDraft({
        planResultRootId,
        planResultTypeId,
        builderDraft: buildDraftSavePayload(
          builderDraft,
          primaryMeetingObjective,
          agendaSections,
        ),
      }).unwrap();
      setSaveSuccess(true);
      setIsPrepOpen(false);
    } catch {
      setSaveSuccess(false);
    }
  };

  const saveErrorMessage = String(
    (saveError as { data?: { error?: string } } | undefined)?.data?.error ??
      'Failed to save meeting intake.',
  );

  const handleClose = () => {
    window.close();
    window.setTimeout(() => {
      navigate(financialPlanningPages.clientListUrl);
    }, 100);
  };

  const handleViewSignalCatalog = () => {
    navigate(
      financialPlanningPages.getMeetingGuidanceSignalCatalogUrl(
        planResultRootId,
        planResultTypeId,
      ),
    );
  };

  const updateAgendaSection = (
    sectionId: string,
    updates: Partial<MeetingGuidanceBuilderSectionDraft>,
  ) => {
    setAgendaSections((sections) =>
      sections.map((section) =>
        section.section_id === sectionId ? { ...section, ...updates } : section,
      ),
    );
  };

  const handleSaveAgendaBuilder = async () => {
    setSaveSuccess(false);
    try {
      await saveBuilderDraft({
        planResultRootId,
        planResultTypeId,
        builderDraft: buildDraftSavePayload(
          builderDraft,
          primaryMeetingObjective,
          agendaSections,
        ),
      }).unwrap();
      setSaveSuccess(true);
    } catch {
      setSaveSuccess(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100vw',
        minHeight: '100vh',
        backgroundColor: 'grey.100',
      }}
    >
      <Box sx={{ p: 4, pb: 0 }}>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 2,
                }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="h5SMedium">Prepare Meeting</Typography>
                  <Typography variant="par02Regular" color="text.secondary">
                    Edit meeting intent and template inputs before using the prep brief
                    and live companion.
                  </Typography>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button variant="outlined" onClick={handleViewSignalCatalog}>
                    View Signal Catalog
                  </Button>
                  <Button variant="outlined" onClick={() => setIsPrepOpen(!isPrepOpen)}>
                    {isPrepOpen ? 'Hide Prep Inputs' : 'Edit Prep Inputs'}
                  </Button>
                </Stack>
              </Box>
              {!isPrepOpen && (
                <Typography variant="par02Regular" color="text.secondary">
                  {prepSummary}
                </Typography>
              )}
              <Typography variant="par02Regular" color="text.secondary">
                Save meeting intake before generating meeting-ready agenda, AI
                questions, and live companion output.
              </Typography>
              {saveSuccess && <Alert severity="success">Meeting intake saved.</Alert>}
              {isSaveError && <Alert severity="error">{saveErrorMessage}</Alert>}
              <Collapse in={isPrepOpen}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="What do you want to accomplish in this meeting?"
                    value={primaryMeetingObjective}
                    onChange={(event) =>
                      setPrimaryMeetingObjective(event.target.value)
                    }
                  />
                  <FormControl fullWidth>
                    <InputLabel id="meeting-template-label">Template</InputLabel>
                    <Select
                      labelId="meeting-template-label"
                      label="Template"
                      value={selectedTemplateId}
                      disabled={templatesLoading}
                      onChange={(event) => setSelectedTemplateId(event.target.value)}
                    >
                      <MenuItem value="">Default agenda</MenuItem>
                      {templatesData?.templates.map((template) => (
                        <MenuItem key={template.template_id} value={template.template_id}>
                          {template.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Client-entered topics"
                      helperText="One topic per line. Client topics default to high priority."
                      value={clientTopics}
                      onChange={(event) => setClientTopics(event.target.value)}
                    />
                    <FormControl sx={{ minWidth: 180 }}>
                      <InputLabel id="client-priority-label">Client Priority</InputLabel>
                      <Select
                        labelId="client-priority-label"
                        label="Client Priority"
                        value={clientPriority}
                        onChange={(event) =>
                          setClientPriority(event.target.value as MeetingIntakePriority)
                        }
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Advisor-entered priorities"
                      helperText="One priority per line"
                      value={advisorPriorities}
                      onChange={(event) => setAdvisorPriorities(event.target.value)}
                    />
                    <FormControl sx={{ minWidth: 180 }}>
                      <InputLabel id="advisor-priority-label">Advisor Priority</InputLabel>
                      <Select
                        labelId="advisor-priority-label"
                        label="Advisor Priority"
                        value={advisorPriority}
                        onChange={(event) =>
                          setAdvisorPriority(event.target.value as MeetingIntakePriority)
                        }
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  <Box>
                    <Button variant="contained" onClick={handleSave} disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save Prep Inputs'}
                    </Button>
                  </Box>
                </Stack>
              </Collapse>
            </Stack>
          </CardContent>
        </Card>
      </Box>
      <Box sx={{ p: 4, pb: 0 }}>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h5SMedium">Agenda Builder</Typography>
              <Typography variant="par02Regular" color="text.secondary">
                Edit section order, visibility, timing, client-facing summaries,
                and advisor talking points. Saved agenda state feeds the meeting
                companion and later presentation output.
              </Typography>
              {agendaSections.map((section) => (
                <Card key={section.section_id} variant="outlined">
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                          label="Order"
                          type="number"
                          sx={{ width: 120 }}
                          value={section.order}
                          onChange={(event) =>
                            updateAgendaSection(section.section_id, {
                              order: Number(event.target.value),
                            })
                          }
                        />
                        <TextField
                          fullWidth
                          label="Section title"
                          value={section.title}
                          onChange={(event) =>
                            updateAgendaSection(section.section_id, {
                              title: event.target.value,
                            })
                          }
                        />
                        <TextField
                          label="Minutes"
                          type="number"
                          sx={{ width: 140 }}
                          value={section.estimated_minutes ?? ''}
                          onChange={(event) =>
                            updateAgendaSection(section.section_id, {
                              estimated_minutes: event.target.value
                                ? Number(event.target.value)
                                : null,
                            })
                          }
                        />
                        <FormControl sx={{ minWidth: 160 }}>
                          <InputLabel id={`${section.section_id}-visibility-label`}>
                            Visibility
                          </InputLabel>
                          <Select
                            labelId={`${section.section_id}-visibility-label`}
                            label="Visibility"
                            value={section.is_visible ? 'visible' : 'hidden'}
                            disabled={section.agenda_item_id === 'welcome_overview'}
                            onChange={(event) =>
                              updateAgendaSection(section.section_id, {
                                is_visible: event.target.value === 'visible',
                              })
                            }
                          >
                            <MenuItem value="visible">Visible</MenuItem>
                            <MenuItem value="hidden">Hidden</MenuItem>
                          </Select>
                        </FormControl>
                      </Stack>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Client-facing summary"
                        value={section.client_facing_summary ?? ''}
                        onChange={(event) =>
                          updateAgendaSection(section.section_id, {
                            client_facing_summary: event.target.value,
                          })
                        }
                      />
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Advisor talking points"
                        helperText="One talking point per line. These remain advisor-facing."
                        value={section.advisor_talking_points.join('\n')}
                        onChange={(event) =>
                          updateAgendaSection(section.section_id, {
                            advisor_talking_points: event.target.value
                              .split('\n')
                              .map((line) => line.trim())
                              .filter(Boolean),
                          })
                        }
                      />
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              <Box>
                <Button variant="contained" onClick={handleSaveAgendaBuilder}>
                  Save Agenda Builder
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box>
      {isMeetingReady ? (
        <MeetingGuidanceWidget
          planResultRootId={planResultRootId}
          planResultTypeId={planResultTypeId}
          onClose={handleClose}
        />
      ) : (
        <Box sx={{ p: 4 }}>
          <Alert severity="info">
            Complete and save meeting intake before viewing meeting-ready agenda,
            AI questions, and live companion output.
          </Alert>
        </Box>
      )}
    </Box>
  );
};
